// src/AdminCommunicationTab.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminCommunicationTab = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchCommunicationAnalytics();
  }, []);

  const fetchCommunicationAnalytics = async () => {
    try {
      setLoading(true);
      const response = await authedFetch(`${apiUrl('adminAnalytics')}?type=communication`);
      const result = await response.json();
      
      if (result.success) {
        setAnalytics(result.data);
        setLastUpdated(new Date(result.timestamp));
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch communication analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Loading communication analytics...</div>;
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <p>Error: {error}</p>
        <button onClick={fetchCommunicationAnalytics} style={refreshButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Communication Analytics</h2>
        <div style={refreshContainerStyle}>
          <button onClick={fetchCommunicationAnalytics} style={refreshButtonStyle}>
            Refresh Data
          </button>
          {lastUpdated && (
            <span style={timestampStyle}>
              Last updated: {lastUpdated.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Communication Health Score */}
      <div style={healthScoreStyle}>
        <div style={scoreCircleStyle}>
          <div style={scoreValueStyle}>{analytics.communicationHealthScore}</div>
          <div style={scoreLabelStyle}>Health Score</div>
        </div>
        <div style={scoreDescStyle}>
          Overall communication system performance based on delivery rates, 
          contact coverage, and opt-out rates.
        </div>
      </div>

      {/* Delivery Performance */}
      <div style={sectionStyle}>
        <h3>Delivery Performance</h3>
        <div style={deliveryGridStyle}>
          <div style={deliveryCardStyle}>
            <div style={deliveryIconStyle}>📧</div>
            <div style={deliveryLabelStyle}>Email Delivery</div>
            <div style={deliveryRateStyle}>{analytics.emailDeliveryRate}%</div>
            <div style={deliveryStatsStyle}>
              {analytics.deliveryStats.deliveredEmails} delivered / {analytics.deliveryStats.totalEmails} sent
            </div>
            {analytics.deliveryStats.failedEmails > 0 && (
              <div style={failedStyle}>
                {analytics.deliveryStats.failedEmails} failed
              </div>
            )}
          </div>
          <div style={deliveryCardStyle}>
            <div style={deliveryIconStyle}>📱</div>
            <div style={deliveryLabelStyle}>SMS Delivery</div>
            <div style={deliveryRateStyle}>{analytics.smsDeliveryRate}%</div>
            <div style={deliveryStatsStyle}>
              {analytics.deliveryStats.deliveredSMS} delivered / {analytics.deliveryStats.totalSMS} sent
            </div>
            {analytics.deliveryStats.failedSMS > 0 && (
              <div style={failedStyle}>
                {analytics.deliveryStats.failedSMS} failed
              </div>
            )}
          </div>
        </div>
        
        {analytics.deliveryStats.totalEmails === 0 && analytics.deliveryStats.totalSMS === 0 && (
          <div style={noDataStyle}>
            📊 Delivery tracking will show here once outbox webhooks are implemented
          </div>
        )}
      </div>

      {/* Communication Preferences */}
      <div style={sectionStyle}>
        <h3>Member Communication Preferences</h3>
        <div style={prefsGridStyle}>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>📧📱</div>
            <div style={prefLabelStyle}>Both Email & SMS</div>
            <div style={prefValueStyle}>{analytics.communicationPrefs.both}</div>
            <div style={prefPercentStyle}>
              {analytics.preferencesPercentages.both}% of members
            </div>
          </div>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>📧</div>
            <div style={prefLabelStyle}>Email Only</div>
            <div style={prefValueStyle}>{analytics.communicationPrefs.email}</div>
            <div style={prefPercentStyle}>
              {analytics.preferencesPercentages.email}% of members
            </div>
          </div>
          <div style={prefCardStyle}>
            <div style={prefIconStyle}>📱</div>
            <div style={prefLabelStyle}>SMS Only</div>
            <div style={prefValueStyle}>{analytics.communicationPrefs.text}</div>
            <div style={prefPercentStyle}>
              {analytics.preferencesPercentages.text}% of members
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Coverage */}
      <div style={sectionStyle}>
        <h3>Contact Information Coverage</h3>
        <div style={contactGridStyle}>
          <div style={contactItemStyle}>
            <span style={contactLabelStyle}>Members with Email:</span>
            <span style={contactValueStyle}>
              {analytics.contactInfo.membersWithEmail} / {analytics.contactInfo.totalMembers}
            </span>
          </div>
          <div style={contactItemStyle}>
            <span style={contactLabelStyle}>Members with Phone:</span>
            <span style={contactValueStyle}>
              {analytics.contactInfo.membersWithPhone} / {analytics.contactInfo.totalMembers}
            </span>
          </div>
          <div style={contactItemStyle}>
            <span style={contactLabelStyle}>Members with Both:</span>
            <span style={contactValueStyle}>
              {analytics.contactInfo.membersWithBoth} / {analytics.contactInfo.totalMembers}
            </span>
          </div>
          <div style={contactItemStyle}>
            <span style={contactLabelStyle}>Coverage Rate:</span>
            <span style={contactValueStyle}>
              {Math.round((analytics.contactInfo.membersWithBoth / analytics.contactInfo.totalMembers) * 100)}%
            </span>
          </div>
        </div>
      </div>

      {/* Opt-out Analysis */}
      <div style={sectionStyle}>
        <h3>Opt-out Analysis</h3>
        <div style={optOutGridStyle}>
          <div style={optOutCardStyle}>
            <div style={optOutLabelStyle}>Total Opt-outs</div>
            <div style={optOutValueStyle}>{analytics.optOutAnalysis.totalOptOuts}</div>
            <div style={optOutDescStyle}>
              Members who changed from "both" to single channel
            </div>
          </div>
          <div style={optOutCardStyle}>
            <div style={optOutLabelStyle}>Opted Out of SMS</div>
            <div style={optOutValueStyle}>{analytics.optOutAnalysis.optedOutOfSMS}</div>
            <div style={optOutDescStyle}>
              Now receive email only
            </div>
          </div>
          <div style={optOutCardStyle}>
            <div style={optOutLabelStyle}>Opted Out of Email</div>
            <div style={optOutValueStyle}>{analytics.optOutAnalysis.optedOutOfEmail}</div>
            <div style={optOutDescStyle}>
              Now receive SMS only
            </div>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div style={sectionStyle}>
        <h3>Communication System Status</h3>
        <div style={statusGridStyle}>
          <div style={statusItemStyle}>
            <span style={statusLabelStyle}>Email System:</span>
            <span style={{...statusValueStyle, color: analytics.deliveryStats.totalEmails > 0 ? '#22c55e' : '#f59e0b'}}>
              {analytics.deliveryStats.totalEmails > 0 ? 'Active' : 'Pending Setup'}
            </span>
          </div>
          <div style={statusItemStyle}>
            <span style={statusLabelStyle}>SMS System:</span>
            <span style={{...statusValueStyle, color: analytics.deliveryStats.totalSMS > 0 ? '#22c55e' : '#f59e0b'}}>
              {analytics.deliveryStats.totalSMS > 0 ? 'Active' : 'Pending Setup'}
            </span>
          </div>
          <div style={statusItemStyle}>
            <span style={statusLabelStyle}>Webhook Tracking:</span>
            <span style={{...statusValueStyle, color: '#f59e0b'}}>
              Ready for Implementation
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
  paddingBottom: '1rem',
  borderBottom: '2px solid #e5e7eb'
};

const refreshContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const refreshButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#1976d2',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer'
};

const timestampStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const healthScoreStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem',
  display: 'flex',
  alignItems: 'center',
  gap: '2rem'
};

const scoreCircleStyle = {
  width: '120px',
  height: '120px',
  borderRadius: '50%',
  backgroundColor: '#f0f9ff',
  border: '4px solid #3b82f6',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center'
};

const scoreValueStyle = {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: '#3b82f6'
};

const scoreLabelStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const scoreDescStyle = {
  flex: 1,
  fontSize: '1.1rem',
  color: '#666',
  lineHeight: '1.5'
};

const sectionStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const deliveryGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginTop: '1rem'
};

const deliveryCardStyle = {
  textAlign: 'center',
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px'
};

const deliveryIconStyle = {
  fontSize: '2rem',
  marginBottom: '0.5rem'
};

const deliveryLabelStyle = {
  fontSize: '1.1rem',
  fontWeight: '600',
  marginBottom: '0.5rem'
};

const deliveryRateStyle = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#22c55e',
  marginBottom: '0.5rem'
};

const deliveryStatsStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const failedStyle = {
  fontSize: '0.85rem',
  color: '#ef4444',
  marginTop: '0.25rem'
};

const noDataStyle = {
  textAlign: 'center',
  padding: '2rem',
  color: '#666',
  fontStyle: 'italic',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  marginTop: '1rem'
};

const prefsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1.5rem',
  marginTop: '1rem'
};

const prefCardStyle = {
  textAlign: 'center',
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '10px'
};

const prefIconStyle = {
  fontSize: '2rem',
  marginBottom: '0.5rem'
};

const prefLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#666',
  marginBottom: '0.5rem'
};

const prefValueStyle = {
  fontSize: '1.8rem',
  fontWeight: 'bold',
  color: '#3b82f6'
};

const prefPercentStyle = {
  fontSize: '0.85rem',
  color: '#666',
  marginTop: '0.25rem'
};

const contactGridStyle = {
  marginTop: '1rem'
};

const contactItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px',
  marginBottom: '0.5rem'
};

const contactLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600'
};

const contactValueStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  color: '#3b82f6'
};

const optOutGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1.5rem',
  marginTop: '1rem'
};

const optOutCardStyle = {
  textAlign: 'center',
  padding: '1.5rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px'
};

const optOutLabelStyle = {
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#666',
  marginBottom: '0.5rem'
};

const optOutValueStyle = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#ef4444',
  marginBottom: '0.5rem'
};

const optOutDescStyle = {
  fontSize: '0.8rem',
  color: '#666'
};

const statusGridStyle = {
  marginTop: '1rem'
};

const statusItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px',
  marginBottom: '0.5rem'
};

const statusLabelStyle = {
  fontSize: '1rem',
  fontWeight: '600'
};

const statusValueStyle = {
  fontSize: '1rem',
  fontWeight: 'bold'
};

const loadingStyle = {
  padding: '2rem',
  textAlign: 'center',
  fontSize: '1.2rem'
};

const errorStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: '#d32f2f'
};

export default AdminCommunicationTab;